<script setup lang="ts">
import { ref, onMounted } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { IRole } from '../../types/user.type';
import UserApi from '../../repositories/user.repository';
import ModalContentRolesCreate from './Usuarios/ModalContentRolesCreate.vue';
import ModalContentRolesUpdate from './Usuarios/ModalContentRolesUpdate.vue';
import { useI18n } from 'vue-i18n'
import DataTable from '@components/DataTable.vue';

const isLoadingRoles = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { roles: rolesPinia }: any = storeToRefs(store);

const roles = ref<IRole[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedRole = ref<IRole | null>(null);
const userApiRepository = new UserApi();

const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'title',
    label: t('global.nombre'),
  },
  {
    key: 'description',
    label: t('global.descripcion'),
  },
  {
    key: 'roleInfo',
    label: t('global.editar'),
  },
]);

// Load Countries ABM
const getRolesFromAPI = async (page: number) => {
  isLoadingRoles.value = true;
  try {
    const data = await userApiRepository.getRoles({ page });

    if (data && Array.isArray(data.roles)) {
      paginationData.value = data.pagination;
      return data.roles;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching roles:', error);
    return [];
  } finally {
    isLoadingRoles.value = false;
  }
};

const updateRoles = (newRoles: IRole[]) => {
  roles.value = newRoles;
  store.setProp({ key: 'roles', value: newRoles });
};

const loadRoles = async () => {
  const newRoles = await getRolesFromAPI(1);
  updateRoles(newRoles);
};

onMounted(loadRoles);

// UI
const onChangePage = async (page: number) => {
  getRolesFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.roles.agregar_rol');
  isModalOpen.value = true;
  modalType.value = 'modalCreateRol';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedRole.value = null;
};

const handleSelectRol = (rolData: IRole) => {
  modalTitle.value = t('ajustes.roles.actualizar_rol');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateRol');
  selectedRole.value = rolData;
};
</script>

<template>
  <Layout :title="t('ajustes.roles.roles')" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-end">
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>{{ t('ajustes.roles.agregar_rol') }}</template>
        </CButton>
      </div>

      <DataTable
        :loading="isLoadingRoles"
        :fields="fieldsTableActive"
        :items="rolesPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(roleInfo)="rolData">
          <div @click="handleSelectRol(rolData.data.item)" class="button-icon">
            <img src="/icons/editar.svg" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
        size="xl"
      >
        <template v-if="modalType === 'modalCreateRol'">
          <ModalContentRolesCreate @close-modal="handleClose" />
        </template>

        <template v-if="modalType === 'modalUpdateRol' && selectedRole">
          <ModalContentRolesUpdate
            :role-id="selectedRole.id"
            @close-modal="handleClose"
          />
        </template>
      </BModal>
    </template>
  </Layout>
</template>
